/**
 * Chat Interface Component
 * Main chat container that combines MessageList, Suggested Prompts,
 * and InputArea
 */

import { MessageList } from './MessageList';
import { InputArea } from './InputArea';
import { EmptyStateWithPrompts }
  from './SuggestedPrompts';
import { ProcessingStatus, type ProcessingStep } from './ProcessingStatus';
import { AgentSelector } from '@/components/Agents';
import { cn } from '@/utils/classNames';
import { useAdmin } from '@/context/AdminContext';
import type { Message, MessageFeedback } from '@/types';

export interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (content: string, files?: FileList) => void;
  onFileUpload?: (files: FileList) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  processingStatus?: ProcessingStep | null;
  /**
   * Epoch ms when the in-flight chat completion was kicked off. Used by
   * MessageList's typing indicator to surface an elapsed-time hint
   * once the wait crosses a threshold (so slow Model Router calls
   * don't look like a hang). Null when nothing is in flight.
   */
  generationStartedAt?: number | null;
  /**
   * Invoked when the user presses Stop. Wired down to InputArea so
   * the Send button flips into a Stop button while loading.
   */
  onCancelGeneration?: () => void;
  /** Forwarded to MessageList → Message for feedback telemetry. */
  conversationId?: string | null;
  /** Forwarded to MessageList → Message for feedback telemetry. */
  agentId?: string | null;
  /** Forwarded to MessageList → Message for feedback telemetry. */
  model?: string | null;
  /** Bubbled up from the thumbs handler so the parent can persist. */
  onFeedbackChange?: (
    messageId: string,
    feedback: MessageFeedback | null
  ) => void;
  /** Opens the in-app user guide from the empty-state "new here?" link. */
  onOpenGuide?: () => void;
}

export function ChatInterface({
  messages,
  onSendMessage,
  onFileUpload,
  isLoading = false,
  disabled = false,
  className,
  processingStatus,
  generationStartedAt = null,
  onCancelGeneration,
  conversationId,
  agentId,
  model,
  onFeedbackChange,
  onOpenGuide,
}: ChatInterfaceProps) {
  const { isAdmin, config } = useAdmin();
  const hasMessages = messages.length > 0;

  // Show agent selector if:
  // 1. User is admin, OR
  // 2. Admin has enabled agent selection for users
  const showAgentSelector = isAdmin || config.agentControl.enableAgentSelection;

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-bg-primary',
        className
      )}
    >
      {/* Agent Selector - Visible to admins or when enabled for users */}
      {showAgentSelector && (
        <div className="border-b border-border-default bg-bg-secondary px-4 py-3">
          <AgentSelector />
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        {hasMessages ? (
          <MessageList
            messages={messages}
            isLoading={isLoading}
            generationStartedAt={generationStartedAt}
            conversationId={conversationId}
            agentId={agentId}
            model={model}
            onFeedbackChange={onFeedbackChange}
          />
        ) : (
          <EmptyStateWithPrompts
            onSelectPrompt={onSendMessage}
            onOpenGuide={onOpenGuide}
          />
        )}
      </div>

      {/* Processing Status - Show when AI is working */}
      {processingStatus && (
        <div className="border-t border-border-default bg-bg-secondary px-4 py-3">
          <ProcessingStatus step={processingStatus} />
        </div>
      )}

      {/* Input — `disabled` keeps the textarea read-only while a
          completion is in flight, but the Send button still flips into
          a Stop button (driven by `isLoading + onCancelGeneration`)
          so the user can abort without having to type. */}
      <InputArea
        onSendMessage={onSendMessage}
        onFileUpload={onFileUpload}
        disabled={disabled || isLoading}
        isLoading={isLoading}
        onCancelGeneration={onCancelGeneration}
      />
    </div>
  );
}
